import { DOCUMENT_KIND_LABELS, hasDesiSlot, withDesi, type DocumentKind } from '@wms/domain';
import { useEffect, useState } from 'react';
import { api, apiBlob, errorMessage } from '../api';
import { Modal, Spinner, useToast } from './index';
import { Icon } from './icons';
import { printPdf, printZpl } from '../printing';
import { ZplPreview } from './ZplPreview';

export type SellerDocumentKind = 'e_waybill' | 'e_archive_invoice';

const MAX_BYTES = 5 * 1024 * 1024;

/** The seller uploads the e-İrsaliye / e-Arşiv PDF their integrator issued for an order. */
export function UploadDocumentDialog(props: { orderId: string; orderNumber: string; kind: SellerDocumentKind; onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [number, setNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return setError('PDF dosyasını seçin');
    if (file.size > MAX_BYTES) return setError('Dosya 5 MB üzerinde');
    setBusy(true);
    setError(null);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      let binary = '';
      for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      await api('PUT', `/api/orders/${props.orderId}/documents/${props.kind}`, { format: 'PDF', contentBase64: btoa(binary), documentNumber: number.trim() });
      props.onDone();
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`${DOCUMENT_KIND_LABELS[props.kind]} yükle · ${props.orderNumber}`}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || !file || number.trim().length < 3} onClick={() => void submit()}>
            <Icon name="upload" /> Yükle
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        <div className="field">
          <label htmlFor="doc-file">PDF dosyası</label>
          <input id="doc-file" className="input" type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} style={{ paddingTop: 9 }} />
        </div>
        <div className="field">
          <label htmlFor="doc-number">Belge numarası</label>
          <input id="doc-number" className="input mono" value={number} onChange={(e) => setNumber(e.target.value.toUpperCase())} placeholder="IRS2026000000123" />
        </div>
        <p className="muted" style={{ fontSize: 13 }}>
          Belge siparişe eklenir; paketleme masasında koliye basılır ve paketleme bu belge olmadan tamamlanmaz (ayarlarınızda zorunluysa).
        </p>
      </div>
    </Modal>
  );
}

/** A desi as typed ("3", "3,5"), or null. */
export const desiText = (raw: string): string | null => (/^\d{1,3}([.,]\d{1,2})?$/.test(raw.trim()) ? raw.trim().replace('.', ',') : null);

/**
 * Shows a stored document (PDFs in the browser's viewer, ZPL labels drawn as they print) and prints
 * it. Our own shipping labels print no desi; "Desi ekle" writes one in before printing.
 */
export function DocumentPreview(props: { orderId: string; orderNumber: string; kind: DocumentKind; onClose: () => void; desi?: boolean }) {
  const [state, setState] = useState<{ url: string; type: string; text: string | null; blob: Blob } | { error: string } | null>(null);
  const toast = useToast();
  const [printing, setPrinting] = useState(false);
  // the desi written onto our label for this print (null: none)
  const [adding, setAdding] = useState(props.desi === true);
  const [desiRaw, setDesiRaw] = useState('');
  const desi = desiText(desiRaw);
  const slot = state !== null && !('error' in state) && state.text !== null && hasDesiSlot(state.text);
  const zpl = state && !('error' in state) && state.text !== null ? (slot && adding && desi ? withDesi(state.text, desi) : state.text) : null;
  useEffect(() => {
    let url: string | null = null;
    apiBlob(`/api/orders/${props.orderId}/documents/${props.kind}`)
      .then(async (blob) => {
        url = URL.createObjectURL(blob);
        setState({ url, type: blob.type, text: blob.type.startsWith('text/') ? await blob.text() : null, blob });
      })
      .catch((e) => setState({ error: errorMessage(e) }));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [props.orderId, props.kind]);

  // through this computer's printer driver: the thermal printer at the desk (docs/yazici-kurulumu.md)
  const print = async () => {
    if (!state || 'error' in state) return;
    setPrinting(true);
    try {
      if (zpl?.includes('^XA')) {
        const { warnings } = await printZpl(zpl, `${DOCUMENT_KIND_LABELS[props.kind]} ${props.orderNumber}`);
        if (warnings.length > 0) toast(`Yazdırıldı; yaklaşık çizilen: ${warnings.join(', ')}`, 'info');
      } else await printPdf(state.blob);
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal
      title={`${DOCUMENT_KIND_LABELS[props.kind]} · ${props.orderNumber}`}
      onClose={props.onClose}
      wide
      footer={
        state && !('error' in state) ? (
          <>
            {slot &&
              (adding ? (
                <span className="row" style={{ gap: 6, marginRight: 'auto' }}>
                  <label htmlFor="label-desi" className="muted">
                    Desi
                  </label>
                  <input
                    id="label-desi"
                    className={`input${desiRaw && !desi ? ' invalid' : ''}`}
                    style={{ width: 90 }}
                    inputMode="decimal"
                    autoFocus
                    placeholder="ör. 3"
                    value={desiRaw}
                    onChange={(e) => setDesiRaw(e.target.value)}
                  />
                  <button className="btn ghost sm" onClick={() => (setAdding(false), setDesiRaw(''))}>
                    Desisiz
                  </button>
                </span>
              ) : (
                <button className="btn" style={{ marginRight: 'auto' }} onClick={() => setAdding(true)} title="Etikete desi yazıp öyle basar">
                  Desi ekle
                </button>
              ))}
            <button className="btn primary" disabled={printing || (adding && slot && !desi)} onClick={() => void print()}>
              <Icon name="printer" /> {adding && slot ? 'Desiyle yazdır' : 'Yazdır'}
            </button>
          </>
        ) : undefined
      }
    >
      {!state ? (
        <Spinner />
      ) : 'error' in state ? (
        <div className="banner bad">{state.error}</div>
      ) : zpl !== null && zpl.includes('^XA') ? (
        // a thermal label: drawn as it prints, the ZPL itself one click away
        <div className="stack">
          <ZplPreview zpl={zpl} />
          <details>
            <summary className="muted">ZPL kodu</summary>
            <pre className="mono" style={{ maxHeight: '40vh', overflow: 'auto', fontSize: 12 }}>
              {state.text}
            </pre>
          </details>
        </div>
      ) : state.text !== null ? (
        <pre className="mono" style={{ maxHeight: '60vh', overflow: 'auto', fontSize: 12 }}>
          {state.text}
        </pre>
      ) : (
        <iframe className="doc-frame" src={state.url} title={DOCUMENT_KIND_LABELS[props.kind]} />
      )}
    </Modal>
  );
}
